/**
 * Elo / Glicko-2 Rating System
 *
 * Used for matchmaking and rankings. Each user has a rating per category:
 *   - overall (composite)
 *   - bench-press, back-squat, deadlift, total
 *
 * New users start at 1200 with high deviation (350).
 * Deviation decreases with more matches, increasing rating confidence.
 * Volatility tracks how erratic performance is.
 *
 * Based on Glicko-2 by Mark Glickman.
 */

import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('ELO');

// ────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────

const DEFAULT_RATING = 1200;
const DEFAULT_DEVIATION = 350;
const DEFAULT_VOLATILITY = 0.06;
const TAU = 0.5;                    // system constant (constrains volatility change)
const CONVERGENCE_TOLERANCE = 0.000001;

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export interface EloRating {
  rating: number;
  deviation: number;
  volatility: number;
}

export interface MatchResult {
  opponentRating: EloRating;
  /** 1 = win, 0.5 = draw, 0 = loss */
  score: number;
}

export interface RatingUpdate {
  newRating: number;
  newDeviation: number;
  newVolatility: number;
  ratingChange: number;
}

// ────────────────────────────────────────────
// Glicko-2 Math
// ────────────────────────────────────────────

/** Convert rating to Glicko-2 scale */
function toGlicko2(rating: number): number {
  return (rating - 1500) / 173.7178;
}

/** Convert Glicko-2 scale back to rating */
function fromGlicko2(mu: number): number {
  return 173.7178 * mu + 1500;
}

/** Convert deviation to Glicko-2 scale */
function deviationToGlicko2(rd: number): number {
  return rd / 173.7178;
}

/** Convert Glicko-2 deviation back */
function deviationFromGlicko2(phi: number): number {
  return 173.7178 * phi;
}

/** g(φ) function */
function g(phi: number): number {
  return 1 / Math.sqrt(1 + 3 * phi * phi / (Math.PI * Math.PI));
}

/** E(μ, μj, φj) expected score */
function E(mu: number, muJ: number, phiJ: number): number {
  return 1 / (1 + Math.exp(-g(phiJ) * (mu - muJ)));
}

/**
 * Calculate new Glicko-2 rating after a series of matches.
 */
export function calculateGlicko2(
  player: EloRating,
  results: MatchResult[],
): RatingUpdate {
  const mu = toGlicko2(player.rating);
  const phi = deviationToGlicko2(player.deviation);
  const sigma = player.volatility;

  if (results.length === 0) {
    // No matches: increase deviation (rating decays toward uncertain)
    const phiStar = Math.sqrt(phi * phi + sigma * sigma);
    return {
      newRating: player.rating,
      newDeviation: Math.min(deviationFromGlicko2(phiStar), DEFAULT_DEVIATION),
      newVolatility: sigma,
      ratingChange: 0,
    };
  }

  // Step 3: Compute v (estimated variance)
  let vInv = 0;
  for (const r of results) {
    const muJ = toGlicko2(r.opponentRating.rating);
    const phiJ = deviationToGlicko2(r.opponentRating.deviation);
    const gJ = g(phiJ);
    const eJ = E(mu, muJ, phiJ);
    vInv += gJ * gJ * eJ * (1 - eJ);
  }
  const v = 1 / vInv;

  // Step 4: Compute delta
  let deltaSum = 0;
  for (const r of results) {
    const muJ = toGlicko2(r.opponentRating.rating);
    const phiJ = deviationToGlicko2(r.opponentRating.deviation);
    const gJ = g(phiJ);
    const eJ = E(mu, muJ, phiJ);
    deltaSum += gJ * (r.score - eJ);
  }
  const delta = v * deltaSum;

  // Step 5: Compute new volatility (iterative)
  const a = Math.log(sigma * sigma);
  const deltaSq = delta * delta;
  const phiSq = phi * phi;

  function f(x: number): number {
    const eX = Math.exp(x);
    const num1 = eX * (deltaSq - phiSq - v - eX);
    const den1 = 2 * Math.pow(phiSq + v + eX, 2);
    return num1 / den1 - (x - a) / (TAU * TAU);
  }

  let A = a;
  let B: number;
  if (deltaSq > phiSq + v) {
    B = Math.log(deltaSq - phiSq - v);
  } else {
    let k = 1;
    while (f(a - k * TAU) < 0) k++;
    B = a - k * TAU;
  }

  let fA = f(A);
  let fB = f(B);
  while (Math.abs(B - A) > CONVERGENCE_TOLERANCE) {
    const C = A + (A - B) * fA / (fB - fA);
    const fC = f(C);
    if (fC * fB <= 0) {
      A = B;
      fA = fB;
    } else {
      fA = fA / 2;
    }
    B = C;
    fB = fC;
  }

  const newSigma = Math.exp(A / 2);

  // Step 6: Update deviation
  const phiStar = Math.sqrt(phiSq + newSigma * newSigma);

  // Step 7: Update rating and deviation
  const newPhi = 1 / Math.sqrt(1 / (phiStar * phiStar) + 1 / v);
  const newMu = mu + newPhi * newPhi * deltaSum;

  const newRating = Math.round(fromGlicko2(newMu));
  const newDeviation = Math.round(deviationFromGlicko2(newPhi));

  return {
    newRating,
    newDeviation: Math.min(newDeviation, DEFAULT_DEVIATION),
    newVolatility: newSigma,
    ratingChange: newRating - player.rating,
  };
}

// ────────────────────────────────────────────
// DB Operations
// ────────────────────────────────────────────

/** Get or create a player's Elo rating for a category */
export async function getEloRating(
  profileId: string,
  category = 'overall',
): Promise<EloRating & { wins: number; losses: number; draws: number }> {
  const { data } = await supabase
    .from('elo_ratings')
    .select('rating, deviation, volatility, wins, losses, draws')
    .eq('profile_id', profileId)
    .eq('category', category)
    .maybeSingle();

  if (data) {
    return {
      rating: data.rating,
      deviation: data.deviation,
      volatility: data.volatility,
      wins: data.wins,
      losses: data.losses,
      draws: data.draws,
    };
  }

  return {
    rating: DEFAULT_RATING,
    deviation: DEFAULT_DEVIATION,
    volatility: DEFAULT_VOLATILITY,
    wins: 0,
    losses: 0,
    draws: 0,
  };
}

/** Update a player's Elo rating after match(es) */
export async function updateEloRating(
  profileId: string,
  category: string,
  update: RatingUpdate,
  result: 'win' | 'loss' | 'draw',
  opponentId?: string,
  opponentRating?: number,
  contextType = 'duel',
  contextId?: string,
): Promise<void> {
  const current = await getEloRating(profileId, category);
  const newWins = current.wins + (result === 'win' ? 1 : 0);
  const newLosses = current.losses + (result === 'loss' ? 1 : 0);
  const newDraws = current.draws + (result === 'draw' ? 1 : 0);

  // Upsert rating — use relative increments to reduce race condition impact.
  // If a concurrent write already updated the row, the absolute values would be wrong.
  // We use the ratingChange delta approach: try an atomic increment via RPC first,
  // fall back to absolute upsert only if RPC doesn't exist.
  const { error: rpcError } = await supabase.rpc('increment_elo_rating', {
    p_profile_id: profileId,
    p_category: category,
    p_rating_delta: update.ratingChange,
    p_new_deviation: update.newDeviation,
    p_new_volatility: update.newVolatility,
    p_is_win: result === 'win' ? 1 : 0,
    p_is_loss: result === 'loss' ? 1 : 0,
    p_is_draw: result === 'draw' ? 1 : 0,
  });

  if (rpcError) {
    // RPC not yet deployed — fall back to absolute upsert
    await supabase
      .from('elo_ratings')
      .upsert({
        profile_id: profileId,
        category,
        rating: update.newRating,
        deviation: update.newDeviation,
        volatility: update.newVolatility,
        peak_rating: Math.max(update.newRating, current.rating),
        wins: newWins,
        losses: newLosses,
        draws: newDraws,
        last_match_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, { onConflict: 'profile_id,category' });
  }

  // Record history
  await supabase
    .from('elo_history')
    .insert({
      profile_id: profileId,
      category,
      rating_before: current.rating,
      rating_after: update.newRating,
      opponent_id: opponentId,
      opponent_rating: opponentRating,
      result,
      context_type: contextType,
      context_id: contextId,
    });
}

/**
 * Process a duel result and update both players' ratings.
 */
export async function processDuelResult(
  winnerId: string,
  loserId: string,
  category = 'overall',
  contextId?: string,
): Promise<{ winnerChange: number; loserChange: number }> {
  // Sanity check: winner and loser must be different users
  if (winnerId === loserId) {
    log.error('Self-duel detected — skipping Elo update', { winnerId, loserId });
    return { winnerChange: 0, loserChange: 0 };
  }

  const winnerRating = await getEloRating(winnerId, category);
  const loserRating = await getEloRating(loserId, category);

  const winnerUpdate = calculateGlicko2(winnerRating, [
    { opponentRating: loserRating, score: 1 },
  ]);

  const loserUpdate = calculateGlicko2(loserRating, [
    { opponentRating: winnerRating, score: 0 },
  ]);

  await Promise.all([
    updateEloRating(winnerId, category, winnerUpdate, 'win', loserId, loserRating.rating, 'duel', contextId),
    updateEloRating(loserId, category, loserUpdate, 'loss', winnerId, winnerRating.rating, 'duel', contextId),
  ]);

  log.info('Duel Elo updated', {
    winner: { id: winnerId, change: winnerUpdate.ratingChange },
    loser: { id: loserId, change: loserUpdate.ratingChange },
  });

  return {
    winnerChange: winnerUpdate.ratingChange,
    loserChange: loserUpdate.ratingChange,
  };
}

/** Get leaderboard by Elo rating */
export async function getEloLeaderboard(
  category = 'overall',
  limit = 50,
): Promise<any[]> {
  const { data } = await supabase
    .from('elo_ratings')
    .select('profile_id, rating, deviation, wins, losses, draws, peak_rating')
    .eq('category', category)
    .order('rating', { ascending: false })
    .limit(limit);
  return data ?? [];
}
